'use client';

import React, { useState } from 'react';
import {
    LayoutDashboard,
    Receipt,
    History,
    CreditCard,
    Bell,
    User,
    LogOut,
    ChevronDown,
    ChevronRight,
    LucideIcon
} from 'lucide-react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

interface NavChild {
    name: string;
    href: string;
    icon: LucideIcon;
}

interface NavItem {
    name: string;
    icon: LucideIcon;
    href?: string;
    children?: NavChild[];
}

interface NavGroup {
    group: string;
    items: NavItem[];
}

const walimuridNavItems: NavGroup[] = [
    {
        group: 'Main Dashboard',
        items: [
            { name: 'Dashboard', icon: LayoutDashboard, href: '/walimurid' },
        ]
    },
    {
        group: 'Keuangan Siswa',
        items: [
            { name: 'Tagihan', icon: Receipt, href: '/walimurid/invoices' },
            { name: 'Riwayat Pembayaran', icon: History, href: '/walimurid/history' },
        ]
    },
    {
        group: 'Pengaturan & Akun',
        items: [
            { name: 'Metode Pembayaran', icon: CreditCard, href: '/walimurid/payments' },
            { name: 'Notifikasi', icon: Bell, href: '/walimurid/notifications' },
            { name: 'Profil Saya', icon: User, href: '/walimurid/profile' },
        ]
    }
];

interface SidebarProps {
    className?: string;
    onClose?: () => void;
    collapsed?: boolean;
    setCollapsed?: (collapsed: boolean) => void;
}

export default function WalimuridSidebar({ className = "", onClose, collapsed = false, setCollapsed }: SidebarProps) {
    const pathname = usePathname();
    const [openSubMenus, setOpenSubMenus] = useState<string[]>([]);

    const toggleSubMenu = (name: string) => {
        setOpenSubMenus(prev =>
            prev.includes(name)
                ? prev.filter(item => item !== name)
                : [...prev, name]
        );
    };

    const isSubMenuOpen = (name: string) => openSubMenus.includes(name);

    return (
        <>
            {/* Desktop Sidebar */}
            <aside
                className={`transition-all duration-500 ease-in-out bg-surface border-r border-border-main hidden lg:flex flex-col shadow-2xl shadow-primary/5 ${collapsed ? 'w-24' : 'w-72'
                    } ${className}`}
            >
                {/* Logo Area */}
                <div className={`p-8 mb-4 transition-all duration-500 flex items-center ${collapsed ? 'justify-center' : 'justify-start'}`}>
                    <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-2xl bg-primary flex items-center justify-center shadow-lg shadow-primary/30 shrink-0 group hover:rotate-12 transition-transform">
                            <User className="text-white w-6 h-6" />
                        </div>
                        {!collapsed && (
                            <div className="flex flex-col animate-in fade-in slide-in-from-left-4 duration-500">
                                <span className="font-black text-foreground tracking-tight text-lg uppercase">Portal<span className="text-primary ml-1">Walimurid</span></span>
                                <span className="text-[9px] font-black text-muted uppercase tracking-[0.3em] -mt-0.5">Finance Center</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Navigation Items */}
                <div className="flex-1 overflow-y-auto px-4 space-y-8 custom-scrollbar pb-10">
                    {walimuridNavItems.map((group, groupIdx) => (
                        <div key={groupIdx} className="space-y-4">
                            {!collapsed && (
                                <h3 className="px-4 text-[10px] font-black text-muted uppercase tracking-[0.2em] opacity-50">
                                    {group.group}
                                </h3>
                            )}
                            <div className="space-y-1.5">
                                {group.items.map((item, itemIdx) => {
                                    const isActive = item.href ? pathname === item.href : item.children?.some(child => pathname === child.href);
                                    const isOpen = isSubMenuOpen(item.name);

                                    return (
                                        <div key={itemIdx} className="space-y-1">
                                            {item.href ? (
                                                <Link
                                                    href={item.href}
                                                    onClick={onClose}
                                                    className={`flex items-center gap-4 px-4 py-3.5 rounded-2xl transition-all duration-300 group relative ${isActive
                                                        ? 'bg-primary text-white shadow-xl shadow-primary/20 translate-x-1'
                                                        : 'text-secondary hover:bg-primary-soft hover:text-primary'
                                                        }`}
                                                >
                                                    <item.icon className={`w-5 h-5 transition-transform duration-300 ${isActive ? 'scale-110' : 'group-hover:scale-110'}`} />
                                                    {!collapsed && <span className="text-xs font-bold leading-none">{item.name}</span>}
                                                    {isActive && (
                                                        <div className="absolute right-3 w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                                                    )}
                                                </Link>
                                            ) : (
                                                <div className="space-y-1">
                                                    <button
                                                        onClick={() => toggleSubMenu(item.name)}
                                                        className={`w-full flex items-center gap-4 px-4 py-3.5 rounded-2xl transition-all duration-300 group ${isActive && !isOpen ? 'bg-primary-soft text-primary' : 'text-secondary hover:bg-primary-soft hover:text-primary'
                                                            }`}
                                                    >
                                                        <item.icon className="w-5 h-5" />
                                                        {!collapsed && (
                                                            <>
                                                                <span className="text-xs font-bold grow text-left">{item.name}</span>
                                                                {isOpen ? (
                                                                    <ChevronDown className="w-4 h-4 transition-transform duration-300" />
                                                                ) : (
                                                                    <ChevronRight className="w-4 h-4 transition-transform duration-300" />
                                                                )}
                                                            </>
                                                        )}
                                                    </button>
                                                    {isOpen && !collapsed && (
                                                        <div className="ml-6 space-y-1 border-l-2 border-primary/10 pl-4 animate-in slide-in-from-top-2 duration-300">
                                                            {item.children?.map((child, childIdx) => {
                                                                const isChildActive = pathname === child.href;
                                                                return (
                                                                    <Link
                                                                        key={childIdx}
                                                                        href={child.href}
                                                                        onClick={onClose}
                                                                        className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 ${isChildActive
                                                                            ? 'text-primary font-black bg-primary-soft'
                                                                            : 'text-muted hover:text-primary'
                                                                            }`}
                                                                    >
                                                                        <child.icon className="w-3.5 h-3.5" />
                                                                        <span className="text-[11px] font-bold">{child.name}</span>
                                                                    </Link>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            </aside>
        </>
    );
}
